import type { AchievementUnlock } from "../types";
import { feedback } from "../services/feedback";

export class AchievementToastQueue {
  private readonly host: HTMLElement;
  private active: HTMLElement | null = null;
  private queue: AchievementUnlock[] = [];

  constructor(host: HTMLElement) {
    this.host = host;
  }

  enqueue(unlocks: AchievementUnlock[]): void {
    if (!unlocks || unlocks.length === 0) {
      return;
    }

    this.queue.push(...unlocks);
    this.flush();
  }

  private flush(): void {
    if (this.active || this.queue.length === 0) {
      return;
    }

    const next = this.queue.shift();
    if (!next) {
      return;
    }

    const toast = document.createElement("div");
    toast.className = "achievement-toast";
    toast.innerHTML = `
      <i data-lucide="award" aria-hidden="true"></i>
      <div>
        <p class="eyebrow">Achievement unlocked</p>
        <strong>${escapeHtml(next.title)}</strong>
        <span>${escapeHtml(next.description)}</span>
      </div>
    `;
    this.host.append(toast);
    this.active = toast;

    void window.requestAnimationFrame(() => toast.classList.add("visible"));
    feedback.achievement(isRareAchievement(next.id) ? "rare" : "routine");

    window.setTimeout(() => {
      toast.classList.remove("visible");
      window.setTimeout(() => {
        toast.remove();
        this.active = null;
        this.flush();
      }, 320);
    }, 3200);
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Rare achievements get the full bell-ladder + sparkle treatment. Everything
// else gets the snappier Mario-coin pop. Curated by id since the server
// doesn't tag rarity directly.
const RARE_ACHIEVEMENTS = new Set<string>([
  "perfect-round",
  "perfect-place-streak-5",
  "ancient-master",
  "modern-master",
  "globetrotter",
  "polyglot",
  "daily-streak-7",
  "session-22k"
]);

function isRareAchievement(id: string): boolean {
  return RARE_ACHIEVEMENTS.has(id);
}
